/** Asia/Seoul — batch_id는 UTC(Z suffix), 표시는 KST로 변환 */
const KST = 'Asia/Seoul';

export function kstWallClockParts(iso: string): {
  y: number;
  m: number;
  d: number;
  h: number;
  min: number;
} | null {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: KST,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
      .formatToParts(d)
      .map((p) => [p.type, p.value]),
  );
  const y = Number(parts.year);
  const m = Number(parts.month);
  const day = Number(parts.day);
  const h = Number(parts.hour);
  const min = Number(parts.min);
  if ([y, m, day, h, min].some((n) => Number.isNaN(n))) return null;
  return { y, m, d: day, h, min };
}

/** analyser SignalBatchID 와 동일: KST 분 단위 + Z 접미사 */
export function batchStampFromCapturedAtKst(iso: string): string | null {
  const p = kstWallClockParts(iso);
  if (!p) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${p.y}${pad(p.m)}${pad(p.d)}T${pad(p.h)}${pad(p.min)}00Z`;
}

type ChartTime = number | string | { year: number; month: number; day: number };

/** lightweight-charts 축·크로스헤어 — 데이터는 UTC epoch, 표시만 KST.
 *  withSeconds 는 초 칸이 있는 선(겹롱 틱)용이다. */
export function formatChartTimeKST(time: ChartTime, withSeconds = false): string {
  if (typeof time === 'object' && time !== null && 'year' in time) {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(time.month)}/${pad(time.day)}`;
  }
  const sec = typeof time === 'number' ? time : Number.parseInt(String(time), 10);
  if (!Number.isFinite(sec)) return '';
  return new Date(sec * 1000).toLocaleString('ko-KR', {
    timeZone: KST,
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' as const } : {}),
    hour12: false,
  });
}

/** ISO/RFC → KST 날짜+시각 (YYYY-MM-DD HH:MM:SS.fff) */
export function fmtKSTDateTimeSec3(iso?: string): string {
  if (!iso) return '–';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const dateParts = new Intl.DateTimeFormat('en-GB', {
    timeZone: KST,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const getD = (type: string) => dateParts.find((p) => p.type === type)?.value ?? '00';
  return `${getD('year')}-${getD('month')}-${getD('day')} ${fmtKSTTimeSec3(iso)}`;
}

/** KST clock string (HH:MM:SS or HH:MM:SS.mmm) → HH:MM:SS.mmm */
export function formatKSTClockSec3(clock?: string): string {
  const raw = clock?.trim();
  if (!raw) return '–';
  const parts = raw.split('.');
  const base = parts[0];
  if (!/^\d{2}:\d{2}:\d{2}$/.test(base)) return raw;
  const digits = (parts[1] ?? '').replace(/\D/g, '');
  const frac = (digits + '000').slice(0, 3);
  return `${base}.${frac}`;
}

/** Kiwoom batch id (20260610EOD) → trade date YYYY-MM-DD */
export function tradeDateFromKiwoomBatchId(batchId?: string | null): string | null {
  const m = batchId?.trim().match(/^(\d{4})(\d{2})(\d{2})/);
  if (!m) return null;
  return `${m[1]}-${m[2]}-${m[3]}`;
}

/** ws0b/data1 time_kst·start_kst — 파일 값 그대로 (zero-padding 보정 없음). */
export function formatKSTClockAsStored(clock?: string): string {
  const raw = clock?.trim();
  return raw || '–';
}

/** KST clock on trade date → chart epoch seconds. */
export function kstClockOnTradeDateToEpochSec(tradeDate: string, clock?: string): number | null {
  const raw = clock?.trim();
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(tradeDate)) return null;
  if (!/^\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(raw)) return null;
  const ms = Date.parse(`${tradeDate}T${raw}+09:00`);
  if (!Number.isFinite(ms)) return null;
  return ms / 1000;
}

export function isoToChartEpochSec(iso?: string): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  return ms / 1000;
}

/** NF1/data1 — start_kst·end_kst 우선 (테이블과 동일), 없으면 ISO. */
export function surgeSegmentChartEpochSec(
  tradeDate: string | null,
  iso?: string,
  kst?: string,
  preferKst?: boolean,
): number | null {
  if (preferKst && tradeDate && kst?.trim()) {
    const t = kstClockOnTradeDateToEpochSec(tradeDate, kst);
    if (t != null) return t;
  }
  const fromIso = isoToChartEpochSec(iso);
  if (fromIso != null) return fromIso;
  if (tradeDate && kst?.trim()) {
    return kstClockOnTradeDateToEpochSec(tradeDate, kst);
  }
  return null;
}

/** NF1/data1 start_kst·end_kst 우선, 없으면 ISO. */
export function formatSurgeSegmentTimeKST(iso?: string, kst?: string, preferKst?: boolean): string {
  if (preferKst && kst?.trim()) return formatKSTClockAsStored(kst);
  if (iso) return fmtKSTTimeSec6(iso);
  if (kst?.trim()) return formatKSTClockAsStored(kst);
  return '–';
}

/** ISO/RFC → KST 시각만 (HH:MM:SS.fff) */
export function fmtKSTTimeSec3(iso?: string): string {
  if (!iso) return '–';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: KST,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  const isoFrac = iso.match(/\.(\d+)/)?.[1];
  const frac = isoFrac
    ? `.${isoFrac.padEnd(3, '0').slice(0, 3)}`
    : `.${String(d.getMilliseconds()).padStart(3, '0')}`;
  return `${get('hour')}:${get('minute')}:${get('second')}${frac}`;
}

/** ISO/RFC → KST 시각만 (HH:MM:SS.ffffff) */
export function fmtKSTTimeSec6(iso?: string): string {
  if (!iso) return '–';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: KST,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  const isoFrac = iso.match(/\.(\d+)/)?.[1];
  const frac = isoFrac
    ? `.${isoFrac.padEnd(6, '0').slice(0, 6)}`
    : `.${String(d.getMilliseconds()).padStart(3, '0')}000`;
  return `${get('hour')}:${get('minute')}:${get('second')}${frac}`;
}

/** 차트 클릭 → 급등 정의 textarea 포맷 (예: 12:13:13.239681: +14.46%) */
export function formatSurgeHintPoint(timeSec: number, fluRt: number): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: KST,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date(Math.floor(timeSec) * 1000));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  const frac = (timeSec - Math.floor(timeSec)).toFixed(6).slice(2);
  const sign = fluRt >= 0 ? '+' : '';
  return `${get('hour')}:${get('minute')}:${get('second')}.${frac}: ${sign}${fluRt.toFixed(2)}%`;
}

/** ISO/RFC → 표시용 KST (테이블·구간 라벨) */
export function fmtKST(iso?: string): string {
  if (!iso) return '–';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', {
    timeZone: KST,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** batch_id 앞 15자(UTC) → 06/04 19:14 KST */
export function formatBatchStampKst(batchId: string): string | null {
  const m = batchId.match(/^([0-9]{4})([0-9]{2})([0-9]{2})T([0-9]{2})([0-9]{2})/);
  if (!m) return null;
  const d = new Date(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:00Z`);
  if (isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: KST,
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  return `${get('month')}/${get('day')} ${get('hour')}:${get('minute')} KST`;
}
